import { useEffect, useRef, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sparkles, Disc3, ExternalLink, Users, Album as AlbumIcon,
  Globe, Play, MapPin, Music as MusicIcon, Radio,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import LastfmBadge from '@/components/LastfmBadge';
import ArtistAvatar from '@/components/ArtistAvatar';
import { useTriggerFocusReturn } from '@/lib/useTriggerFocusReturn';
import { MODAL_DIALOG_MOTION_CLASSES } from '@/lib/motion';

function formatCount(n) {
  if (n == null) return null;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function youtubeEmbedUrl(url) {
  if (!url) return null;
  // Convert various YouTube URL shapes into the /embed/ form
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  if (!m) return null;
  return `https://www.youtube.com/embed/${m[1]}`;
}

export default function ArtistDeepDive({ artistId, open, onClose, seedName, viaLastfm = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  // Nested deep dive — opened when a Last.fm similar artist is clicked.
  // Stacks on top of the current modal; Radix Dialog handles overlay portals.
  const [nestedArtist, setNestedArtist] = useState(null);
  // Ref to the scrollable DialogContent so we can defensively reset scroll
  // to top whenever the modal opens (Radix focus-management can otherwise
  // auto-scroll the dialog to bring its first focusable element into view,
  // which hides the hero and clips the top of the biography paragraph).
  const contentRef = useRef(null);
  // Focus round-trip — restore focus to the "Artist deep dive" button
  // that opened this modal so keyboard users complete the loop.
  const { handleCloseAutoFocus } = useTriggerFocusReturn(open);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setData(null);
    setActiveVideo(null);
    setHeroIdx(0);
    setNestedArtist(null);
    (async () => {
      try {
        let d = null;
        if (artistId) {
          const res = await api.get(`/spotify/artist/${artistId}/deep-dive`);
          d = res.data;
        } else if (seedName) {
          const res = await api.get(`/artist/by-name/${encodeURIComponent(seedName)}`);
          d = res.data;
        }
        if (active) setData(d || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load artist deep dive');
        if (active) setData({});
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [artistId, seedName, open]);

  const sp = data?.artist || null;
  const tadb = data?.theaudiodb?.artist || null;
  const videos = data?.theaudiodb?.music_videos || [];
  const tadbAlbums = data?.theaudiodb?.albums || [];
  const lf = data?.lastfm || {};
  const lfInfo = lf.info || null;
  const lfSimilar = lf.similar || [];
  const lfTopTags = lf.top_tags || [];
  // Wikipedia (via MusicBrainz disambiguation) — the bio/image safety net
  // for artists missing from TheAudioDB and Spotify.
  const wiki = data?.wikipedia || null;
  const mb = data?.musicbrainz || null;

  const name = sp?.name || tadb?.name || wiki?.title || lfInfo?.name || seedName || 'Artist';
  const fanart = tadb?.fanart || [];
  // Image cascade: TheAudioDB hero -> TheAudioDB banner -> Spotify -> Wikipedia
  const heroImage =
    fanart[heroIdx]
    || tadb?.banner
    || (sp?.images || [])[0]?.url
    || wiki?.image
    || wiki?.thumbnail;
  // Portrait cascade: TheAudioDB thumb -> Spotify -> Wikipedia thumbnail
  const avatarImg =
    tadb?.thumb
    || (sp?.images || [])[0]?.url
    || wiki?.thumbnail
    || wiki?.image;
  // Bio cascade: TheAudioDB -> Wikipedia extract -> Last.fm summary
  // Normalize bio text: TheAudioDB's bios often contain runs of \n and odd
  // whitespace that, combined with `whitespace-pre-line`, would create many
  // invisible "blank lines" and break the visual line-clamp. Collapse to a
  // single paragraph with single newlines between distinct paragraphs.
  const _collapseBio = (s) => {
    if (!s) return null;
    return String(s)
      .replace(/\r\n?/g, '\n')        // normalize line endings
      .replace(/\n{2,}/g, '\n\n')      // dedupe 3+ newlines to a single break
      .replace(/[ \t]+\n/g, '\n')      // strip trailing spaces on lines
      .replace(/\n[ \t]+/g, '\n')      // strip leading spaces on lines
      .trim();
  };
  const bioText = _collapseBio(tadb?.bio);
  const bioHtml = !bioText
    ? (wiki?.extract ? null : (lfInfo?.bio_summary || null))
    : null;
  // Wikipedia returns plain text in `extract` (preferred for safe rendering)
  const bioWiki = !bioText && wiki?.extract ? _collapseBio(wiki.extract) : null;

  // auto-cycle fanart hero every 6s
  useEffect(() => {
    if (fanart.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % fanart.length), 6000);
    return () => clearInterval(t);
  }, [fanart.length]);

  const genre = sp?.genres?.[0] || tadb?.genre;
  // Country cascade: TheAudioDB -> MusicBrainz country (e.g. "GB", "ES")
  const country = tadb?.country || mb?.country;
  const label = tadb?.label;
  // Formed-year cascade: TheAudioDB -> MusicBrainz life_span.begin year
  const mbBeginYear = (() => {
    const begin = (mb?.life_span || {}).begin;
    if (!begin) return null;
    const m = String(begin).match(/^(\d{4})/);
    return m ? m[1] : null;
  })();
  const formed = tadb?.formed_year || tadb?.born_year || mbBeginYear;

  return (
    <MotionConfig reducedMotion="user">
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        ref={contentRef}
        className={`max-w-md sm:max-w-3xl w-[95vw] bg-card border-border max-h-[92vh] overflow-y-auto p-0 ${MODAL_DIALOG_MOTION_CLASSES}`}
        data-testid="artist-deep-dive"
        onOpenAutoFocus={(e) => {
          // Prevent Radix's default behavior of focusing the first focusable
          // element inside the dialog. That auto-focus call ends up scrolling
          // the scrollable DialogContent down so the focused element is in
          // view — which hides the hero and clips the bio. We want the modal
          // to always open at scrollTop=0 so users see the hero first.
          e.preventDefault();
          // Defensive: explicitly reset scroll after the next paint in case
          // anything else managed to nudge the scroll position during open.
          requestAnimationFrame(() => {
            if (contentRef.current) contentRef.current.scrollTop = 0;
          });
        }}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{name}</DialogTitle>
          <DialogDescription>Artist info, top tracks, music videos, fanart, discography</DialogDescription>
        </DialogHeader>

        {/* Wrapper: shadcn DialogContent uses `display: grid` with `gap-4`,
            which doesn't size aspect-ratio'd children correctly and causes
            the hero to overlap the content below. Wrapping everything in a
            single flex column makes the grid see only one child and lets
            normal flow correctly stack hero -> sections. */}
        <div className="flex flex-col">

        {/* Hero — when we have a fanart/banner image, render the cinematic
            16:9 banner with portrait + name overlaid at the bottom. When we
            don't (niche artists w/ no TheAudioDB coverage), drop to a slim
            colored band so the modal doesn't open with a giant empty void. */}
        {heroImage ? (
          <div className="relative aspect-[16/9] sm:aspect-[16/7] w-full bg-secondary overflow-hidden">
            <img
              src={heroImage}
              alt={name}
              className="w-full h-full object-cover transition-opacity duration-700"
              key={heroImage}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
            {viaLastfm && (
              <div className="absolute top-3 left-3 z-10">
                <LastfmBadge variant="prominent" href={lfInfo?.url || 'https://www.last.fm/'} />
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex items-end gap-3 sm:gap-4">
              <div className="rounded-2xl overflow-hidden border-2 border-card shrink-0">
                <ArtistAvatar name={name} src={avatarImg} size={96} shape="square" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-2xl sm:text-4xl font-semibold leading-tight drop-shadow-md">
                  {name}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {sp?.followers != null && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {Number(sp.followers).toLocaleString()}
                    </span>
                  )}
                  {lfInfo?.listeners != null && (
                    <span
                      className="text-xs text-muted-foreground inline-flex items-center gap-1"
                      data-testid="artist-lastfm-listeners"
                      title={`${lfInfo.listeners.toLocaleString()} Last.fm listeners`}
                    >
                      <Radio className="w-3 h-3" /> {formatCount(lfInfo.listeners)}
                    </span>
                  )}
                  {country && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {country}
                    </span>
                  )}
                  {formed && (
                    <span className="text-xs text-muted-foreground font-mono">since {formed}</span>
                  )}
                  {label && (
                    <span className="text-[10px] uppercase font-mono text-muted-foreground/70">
                      {label}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Slim gradient banner + inline portrait card for image-less artists.
          // Avoids the giant empty hero placeholder for niche artists.
          <div className="relative">
            <div
              className="h-16 sm:h-20 w-full"
              style={{
                background: `linear-gradient(120deg,
                  hsl(${((name?.charCodeAt(0) || 187) * 7) % 360} 55% 28%) 0%,
                  hsl(${((name?.charCodeAt(0) || 187) * 11) % 360} 50% 14%) 100%)`,
              }}
            />
            {viaLastfm && (
              <div className="absolute top-3 left-3 z-10">
                <LastfmBadge variant="prominent" href={lfInfo?.url || 'https://www.last.fm/'} />
              </div>
            )}
            <div className="px-4 sm:px-6 -mt-10 flex items-end gap-3 sm:gap-4">
              <div className="rounded-2xl overflow-hidden border-2 border-card shrink-0 shadow-lg">
                <ArtistAvatar name={name} src={avatarImg} size={96} shape="square" />
              </div>
              <div className="min-w-0 flex-1 pb-1">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                  {name}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {sp?.followers != null && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {Number(sp.followers).toLocaleString()}
                    </span>
                  )}
                  {lfInfo?.listeners != null && (
                    <span
                      className="text-xs text-muted-foreground inline-flex items-center gap-1"
                      data-testid="artist-lastfm-listeners"
                      title={`${lfInfo.listeners.toLocaleString()} Last.fm listeners`}
                    >
                      <Radio className="w-3 h-3" /> {formatCount(lfInfo.listeners)}
                    </span>
                  )}
                  {country && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {country}
                    </span>
                  )}
                  {formed && (
                    <span className="text-xs text-muted-foreground font-mono">since {formed}</span>
                  )}
                  {label && (
                    <span className="text-[10px] uppercase font-mono text-muted-foreground/70">
                      {label}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 pb-6 sm:pb-8 space-y-5">
          {/* Genre tags */}
          {(sp?.genres?.length > 0 || tadb?.genre || tadb?.style || tadb?.mood) && (
            <div className="flex flex-wrap gap-1.5">
              {(sp?.genres || []).slice(0, 4).map((g) => (
                <Badge key={`s-${g}`} variant="secondary" className="text-[10px] font-mono uppercase">
                  {g}
                </Badge>
              ))}
              {tadb?.genre && (
                <Badge variant="outline" className="text-[10px] font-mono uppercase">
                  {tadb.genre}
                </Badge>
              )}
              {tadb?.style && tadb.style !== tadb.genre && (
                <Badge variant="outline" className="text-[10px] font-mono uppercase">
                  {tadb.style}
                </Badge>
              )}
              {tadb?.mood && (
                <Badge variant="outline" className="text-[10px] font-mono uppercase">
                  {tadb.mood}
                </Badge>
              )}
            </div>
          )}

          {/* Spotify external link */}
          {sp?.external_url && (
            <a
              href={sp.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#1DB954] hover:underline"
            >
              <MusicIcon className="w-3.5 h-3.5" /> Open in Spotify <ExternalLink className="w-3 h-3" />
            </a>
          )}

          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-20 rounded-lg" />
            </div>
          )}

          {/* Biography — cascade: TheAudioDB → Wikipedia → Last.fm */}
          {(bioText || bioWiki || bioHtml) && (
            <section>
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2 inline-flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Biography
              </h3>
              {bioText ? (
                <div
                  className="text-sm leading-relaxed text-foreground/85"
                  data-testid="artist-bio"
                >
                  {bioText.split(/\n\n+/).map((para, i) => (
                    <p key={`bio-p-${i}`} className={i > 0 ? 'mt-2' : ''}>{para}</p>
                  ))}
                </div>
              ) : bioWiki ? (
                <div
                  className="text-sm leading-relaxed text-foreground/85"
                  data-testid="artist-bio-wiki"
                >
                  {bioWiki.split(/\n\n+/).map((para, i) => (
                    <p key={`wbio-p-${i}`} className={i > 0 ? 'mt-2' : ''}>{para}</p>
                  ))}
                </div>
              ) : (
                <div
                  className="text-sm leading-relaxed text-foreground/85"
                  data-testid="artist-bio-lastfm"
                  dangerouslySetInnerHTML={{ __html: bioHtml }}
                />
              )}
              {tadb?.website && (
                <a
                  href={tadb.website.startsWith('http') ? tadb.website : `https://${tadb.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline mt-2"
                >
                  <Globe className="w-3 h-3" /> Official site
                </a>
              )}
              {!tadb?.website && (mb?.urls || {}).official && (
                <a
                  href={mb.urls.official}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline mt-2"
                >
                  <Globe className="w-3 h-3" /> Official site
                </a>
              )}
            </section>
          )}

          {/* Last.fm crowd-sourced tags */}
          {lfTopTags.length > 0 && (
            <section data-testid="artist-lastfm-tags">
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5" /> Listener tags
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {lfTopTags.slice(0, 12).map((t) => (
                  <Badge
                    key={`lf-tt-${t.name}`}
                    variant="outline"
                    className="text-[10px] font-mono lowercase"
                  >
                    {t.name}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {/* Top tracks (Spotify) */}
          {sp?.top_tracks?.length > 0 && (
            <section>
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2">Top tracks</h3>
              <ul className="space-y-1.5">
                {sp.top_tracks.slice(0, 6).map((t, idx) => (
                  <li
                    key={t.id}
                    className="flex items-center gap-3 p-2 rounded-lg border border-border bg-background"
                  >
                    <span className="w-5 text-center text-xs font-mono text-muted-foreground">{idx + 1}</span>
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-secondary shrink-0">
                      {(t.album?.images?.[2] || t.album?.images?.[0])?.url && (
                        <img
                          src={(t.album.images[2] || t.album.images[0]).url}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{t.album?.name}</div>
                    </div>
                    {t.external_url && (
                      <a
                        href={t.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1DB954] hover:opacity-80"
                        aria-label="Open in Spotify"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Music videos (TheAudioDB) */}
          {videos.length > 0 && (
            <section data-testid="artist-music-videos">
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2 inline-flex items-center gap-1">
                <Play className="w-3.5 h-3.5" /> Music videos
              </h3>
              {activeVideo ? (
                <div className="space-y-2">
                  <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
                    <iframe
                      title={activeVideo.track}
                      src={youtubeEmbedUrl(activeVideo.url)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      width="100%"
                      height="100%"
                      frameBorder="0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{activeVideo.track}</div>
                    <button
                      onClick={() => setActiveVideo(null)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      ← Back to videos
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {videos.slice(0, 6).map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setActiveVideo(v)}
                      className="text-left group"
                      data-testid="music-video-tile"
                    >
                      <div className="aspect-video rounded-lg overflow-hidden bg-secondary relative">
                        {v.thumb ? (
                          <img
                            src={v.thumb}
                            alt={v.track}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                          />
                        ) : (
                          <div className="w-full h-full grid place-items-center">
                            <Play className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <Play className="w-8 h-8 text-white fill-white" />
                        </div>
                      </div>
                      <div className="text-[11px] mt-1 truncate">{v.track}</div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Related artists — prefer Spotify, fall back to Last.fm similar (Spotify
              related-artists is deprecated and frequently returns empty/403) */}
          {(sp?.related_artists?.length > 0 || lfSimilar.length > 0) && (
            <section data-testid="artist-related">
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2">
                {sp?.related_artists?.length > 0 ? 'Related artists' : 'Similar artists'}
              </h3>
              <div className="flex gap-3 overflow-x-auto py-3 -mx-1 px-1">
                {sp?.related_artists?.length > 0
                  ? sp.related_artists.slice(0, 8).map((a) => (
                      <a
                        key={a.id}
                        href={a.external_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 w-[60px] text-center group focus:outline-none"
                      >
                        <div className="mb-1.5 mx-auto rounded-full overflow-hidden transition-all duration-200 group-hover:-translate-y-1.5 group-hover:scale-[1.12] group-hover:shadow-[0_8px_20px_-4px_rgba(0,0,0,0.5)] group-hover:ring-2 group-hover:ring-[hsl(var(--primary))]/40">
                          <ArtistAvatar
                            name={a.name}
                            src={(a.images || [])[0]?.url}
                            size={60}
                            shape="circle"
                          />
                        </div>
                        <div className="text-[10px] truncate leading-tight">{a.name}</div>
                      </a>
                    ))
                  : lfSimilar.slice(0, 8).map((a) => (
                      <button
                        key={`lf-sa-${a.url || a.name}`}
                        type="button"
                        onClick={() => setNestedArtist(a)}
                        className="shrink-0 w-[60px] text-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl"
                        data-testid="lastfm-similar-artist"
                        title={`Open ${a.name} deep dive`}
                      >
                        <div className="mb-1.5 mx-auto rounded-full overflow-hidden transition-all duration-200 group-hover:-translate-y-1.5 group-hover:scale-[1.12] group-hover:shadow-[0_8px_20px_-4px_rgba(0,0,0,0.5)] group-hover:ring-2 group-hover:ring-[hsl(var(--primary))]/40">
                          <ArtistAvatar
                            name={a.name}
                            src={a.image}
                            size={60}
                            shape="circle"
                          />
                        </div>
                        <div className="text-[10px] truncate leading-tight group-hover:text-foreground">
                          {a.name}
                        </div>
                      </button>
                    ))}
              </div>
            </section>
          )}

          {/* Discography — prefer TheAudioDB (richer cover art), fall back to Spotify */}
          {(tadbAlbums.length > 0 || sp?.albums?.length > 0) && (
            <section>
              <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2 inline-flex items-center gap-1">
                <AlbumIcon className="w-3.5 h-3.5" /> Discography
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(tadbAlbums.length > 0 ? tadbAlbums : sp.albums).slice(0, 12).map((al) => (
                  <div key={al.id} className="group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-secondary mb-1">
                      {(al.thumb || (al.images || [])[0]?.url) ? (
                        <img
                          src={al.thumb || al.images[0].url}
                          alt={al.name}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                        />
                      ) : null}
                    </div>
                    <div className="text-[11px] truncate">{al.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {al.year || (al.release_date || '').slice(0, 4)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!loading && !sp && !tadb && !lfInfo && !wiki && !mb && (
            <div className="text-sm text-muted-foreground italic">
              No artist data available.
            </div>
          )}

          {/* Attribution footer — credit the providers we actually used.
              Last.fm + MusicBrainz get visible credit. Wikipedia is consulted
              for bios/images but currently shown without an explicit badge
              per product decision (note: CC BY-SA technically requires it). */}
          {(lfInfo || lfSimilar.length > 0 || lfTopTags.length > 0 || mb || wiki) && (
            <div className="pt-3 mt-1 pb-1 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                {(lfInfo || lfSimilar.length > 0 || lfTopTags.length > 0) && (
                  <LastfmBadge
                    variant="inline"
                    href={lfInfo?.url || 'https://www.last.fm/'}
                  />
                )}
                {mb?.mbid && (
                  <a
                    href={`https://musicbrainz.org/artist/${mb.mbid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono text-muted-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1"
                    data-testid="musicbrainz-attribution"
                  >
                    <span>·</span>
                    <span>MusicBrainz</span>
                  </a>
                )}
              </div>
              <div className="flex items-center gap-3">
                {(mb?.urls || {}).discogs && (
                  <a
                    href={mb.urls.discogs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono text-muted-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
                    data-testid="discogs-link"
                  >
                    Discogs <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
                {lfInfo?.url && (
                  <a
                    href={lfInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono text-muted-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
                    data-testid="lastfm-artist-external"
                  >
                    View on Last.fm <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      </DialogContent>

      {/* Nested deep dive — opened when user clicks a Last.fm similar artist.
          Reuses this same component with seedName, so the new modal pulls
          TheAudioDB + Last.fm data for the clicked artist. viaLastfm makes the
          "Powered by Last.fm" badge visible at the top so users understand
          where they landed. */}
      {nestedArtist && (
        <ArtistDeepDive
          key={`nested-${nestedArtist.name}`}
          open={true}
          onClose={() => setNestedArtist(null)}
          seedName={nestedArtist.name}
          viaLastfm
        />
      )}
    </Dialog>
    </MotionConfig>
  );
}
